/**
 * PrivacyPolicy Creation Form Component
 * Reference implementation following UI Standards
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PrivacyPolicyCreate } from '../../types/privacyPolicy';

const PrivacyPolicyForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<PrivacyPolicyCreate>({
    version: '',
    title: '',
    content: '',
    effective_date: '',
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!formData.version || formData.version.trim() === '') errors.version = 'Version is required';
    if (!formData.title || formData.title.trim() === '') errors.title = 'Title is required';
    if (!formData.content || formData.content.trim() === '') errors.content = 'Content is required';
    if (!formData.effective_date || formData.effective_date.trim() === '') errors.effective_date = 'Effective date is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => { const newErrors = { ...prev }; delete newErrors[name]; return newErrors; });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/privacy-policies/', formData);
      setSuccess(true);
      setTimeout(() => navigate('/privacy-policies'), 1500);
    } catch (err: any) {
      console.error('Error creating privacy policy:', err);
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail.map((e: any) => `${e.loc.join('.')}: ${e.msg}`).join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to create privacy policy. Please check your input.');
        }
      } else {
        setError('Failed to create privacy policy. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/privacy-policies')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>Privacy Policies</MuiLink>
        <Typography color="text.primary">New Policy</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Create New Privacy Policy</Typography>
        <Typography variant="body2" color="textSecondary">Enter the privacy policy details</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Privacy policy created successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth required label="Version" name="version"
                value={formData.version} onChange={handleChange}
                error={!!validationErrors.version} helperText={validationErrors.version || 'e.g., 1.0.0'}
                disabled={loading} />
              <TextField fullWidth required type="date" label="Effective Date" name="effective_date"
                value={formData.effective_date} onChange={handleChange}
                error={!!validationErrors.effective_date} helperText={validationErrors.effective_date}
                disabled={loading} InputLabelProps={{ shrink: true }} />
            </Stack>

            <TextField fullWidth required label="Title" name="title"
              value={formData.title} onChange={handleChange}
              error={!!validationErrors.title} helperText={validationErrors.title}
              disabled={loading} />

            <TextField fullWidth required label="Content" name="content"
              value={formData.content} onChange={handleChange}
              error={!!validationErrors.content} helperText={validationErrors.content}
              disabled={loading} multiline rows={10} />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/privacy-policies')} disabled={loading}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}>
                {loading ? 'Creating...' : 'Create Policy'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicyForm;
